import React, { useState, useEffect } from 'react';
import { Navigation } from '../components/Navigation';
import { Newsletter } from '../components/Newsletter';
import { FinancialChart } from '../components/Chart';
import { motion } from 'framer-motion';
import { Calculator, DollarSign, Calendar, Briefcase, Building2, Users, Info } from 'lucide-react';

interface TaxBracket {
  threshold: number;
  rate: number;
}

interface FilingStatus {
  single: TaxBracket[];
  married: TaxBracket[];
  head: TaxBracket[];
}

// 2025 Tax Brackets
const taxBrackets: FilingStatus = {
  single: [
    { threshold: 13850, rate: 0.10 },
    { threshold: 24850, rate: 0.12 },
    { threshold: 58575, rate: 0.22 },
    { threshold: 109225, rate: 0.24 },
    { threshold: 195950, rate: 0.32 },
    { threshold: 245100, rate: 0.35 },
    { threshold: 591975, rate: 0.37 }
  ],
  married: [
    { threshold: 27700, rate: 0.10 },
    { threshold: 49700, rate: 0.12 },
    { threshold: 117150, rate: 0.22 },
    { threshold: 218450, rate: 0.24 },
    { threshold: 391900, rate: 0.32 },
    { threshold: 490200, rate: 0.35 },
    { threshold: 731200, rate: 0.37 }
  ],
  head: [
    { threshold: 20800, rate: 0.10 },
    { threshold: 36500, rate: 0.12 },
    { threshold: 80650, rate: 0.22 },
    { threshold: 116150, rate: 0.24 },
    { threshold: 202900, rate: 0.32 },
    { threshold: 252050, rate: 0.35 },
    { threshold: 598900, rate: 0.37 }
  ]
};

interface WithholdingResult {
  taxableIncome: number;
  annualWithholding: number;
  perPaycheckWithholding: number;
  effectiveRate: number;
  applicableBrackets: Array<{
    rate: number;
    from: number;
    to: number;
    tax: number;
  }>;
}

const WithholdingCalculator: React.FC = () => {
  const [income, setIncome] = useState<number>(50000);
  const [payFrequency, setPayFrequency] = useState<'weekly' | 'biweekly' | 'semimonthly' | 'monthly'>('biweekly');
  const [filingStatus, setFilingStatus] = useState<'single' | 'married' | 'head'>('single');
  const [allowances, setAllowances] = useState<number>(0);
  const [additionalWithholding, setAdditionalWithholding] = useState<number>(0);
  const [deductions, setDeductions] = useState<number>(0);
  const [result, setResult] = useState<WithholdingResult | null>(null);

  const getPayPeriodsPerYear = () => {
    switch (payFrequency) {
      case 'weekly': return 52;
      case 'biweekly': return 26;
      case 'semimonthly': return 24;
      case 'monthly': return 12;
      default: return 26;
    }
  };

  const calculateAnnualIncome = () => {
    return income * getPayPeriodsPerYear();
  };

  const calculateTotalDeductions = () => {
    // Standard deduction for 2025
    const standardDeduction = {
      single: 13850,
      married: 27700,
      head: 20800
    }[filingStatus];

    // Personal allowance calculation
    const allowanceAmount = 4300; // 2025 projection
    const allowanceDeduction = allowances * allowanceAmount;

    return standardDeduction + allowanceDeduction + deductions;
  };

  const calculateWithholding = () => {
    const totalDeductions = calculateTotalDeductions();
    const annualIncome = calculateAnnualIncome();
    const taxableIncome = Math.max(0, annualIncome - totalDeductions);
    const brackets = taxBrackets[filingStatus];
    
    let totalTax = 0;
    let applicableBrackets = [];
    let remainingIncome = taxableIncome;

    // Calculate tax for each bracket progressively
    for (let i = 0; i < brackets.length; i++) {
      const currentBracket = brackets[i];
      const prevBracket = i > 0 ? brackets[i - 1].threshold : 0;
      const bracketIncome = Math.max(0, Math.min(remainingIncome, currentBracket.threshold - prevBracket));
      
      if (bracketIncome > 0) {
        const bracketTax = bracketIncome * currentBracket.rate;
        totalTax += bracketTax;
        
        applicableBrackets.push({
          rate: currentBracket.rate * 100,
          from: prevBracket,
          to: currentBracket.threshold,
          tax: bracketTax
        });
        
        remainingIncome -= bracketIncome;
      }

      // Handle income above the highest bracket
      if (i === brackets.length - 1 && remainingIncome > 0) {
        const finalBracketTax = remainingIncome * currentBracket.rate;
        totalTax += finalBracketTax;
        
        applicableBrackets.push({
          rate: currentBracket.rate * 100,
          from: currentBracket.threshold,
          to: Infinity,
          tax: finalBracketTax
        });
      }
    }

    // Add additional withholding
    totalTax += additionalWithholding * getPayPeriodsPerYear();

    // Calculate per-paycheck withholding
    const perPaycheckWithholding = totalTax / getPayPeriodsPerYear();
    
    // Calculate effective tax rate
    const effectiveRate = (totalTax / taxableIncome) * 100;

    setResult({
      taxableIncome,
      annualWithholding: totalTax,
      perPaycheckWithholding,
      effectiveRate,
      applicableBrackets
    });
  };

  useEffect(() => {
    calculateWithholding();
  }, [income, payFrequency, filingStatus, allowances, additionalWithholding, deductions]);

  const chartData = result ? {
    labels: result.applicableBrackets.map(bracket => 
      bracket.to === Infinity 
        ? `$${bracket.from.toLocaleString()}+`
        : `$${bracket.from.toLocaleString()} - $${bracket.to.toLocaleString()}`
    ),
    datasets: [{
      label: 'Tax Amount',
      data: result.applicableBrackets.map(bracket => bracket.tax),
      backgroundColor: [
        'rgba(16, 185, 129, 0.8)',
        'rgba(52, 211, 153, 0.8)',
        'rgba(5, 150, 105, 0.8)',
        'rgba(4, 120, 87, 0.8)',
        'rgba(6, 95, 70, 0.8)',
        'rgba(6, 78, 59, 0.8)',
        'rgba(4, 47, 35, 0.8)'
      ]
    }]
  } : null;

  return (
    <div className="min-h-screen bg-[var(--background)] p-6">
      <div className="max-w-7xl mx-auto">
        <Navigation />

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-emerald-500/10">
              <Calculator className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="text-4xl font-bold">Tax Withholding Calculator</h1>
          </div>
          <p className="text-slate-400 text-lg">
            Calculate your tax withholding based on 2025 tax brackets
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold mb-6">Income Details</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">Income per Pay Period</label>
                  <div className="relative">
                    <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="number"
                      value={income}
                      onChange={(e) => setIncome(Number(e.target.value))}
                      className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                      min="0"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">Pay Frequency</label>
                  <select
                    value={payFrequency}
                    onChange={(e) => setPayFrequency(e.target.value as typeof payFrequency)}
                    className="glass-input px-4 py-2 w-full rounded-lg"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-Weekly</option>
                    <option value="semimonthly">Semi-Monthly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">Filing Status</label>
                  <select
                    value={filingStatus}
                    onChange={(e) => setFilingStatus(e.target.value as typeof filingStatus)}
                    className="glass-input px-4 py-2 w-full rounded-lg"
                  >
                    <option value="single">Single</option>
                    <option value="married">Married Filing Jointly</option>
                    <option value="head">Head of Household</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">Allowances</label>
                  <input
                    type="number"
                    value={allowances}
                    onChange={(e) => setAllowances(Number(e.target.value))}
                    className="glass-input px-4 py-2 w-full rounded-lg"
                    min="0"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">Additional Withholding</label>
                  <div className="relative">
                    <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="number"
                      value={additionalWithholding}
                      onChange={(e) => setAdditionalWithholding(Number(e.target.value))}
                      className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                      min="0"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">Additional Deductions</label>
                  <div className="relative">
                    <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="number"
                      value={deductions}
                      onChange={(e) => setDeductions(Number(e.target.value))}
                      className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold mb-6">2025 Tax Brackets</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left p-3">Income Range</th>
                      <th className="text-right p-3">Tax Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxBrackets[filingStatus].map((bracket, index) => (
                      <tr key={bracket.threshold} className="border-b border-slate-800">
                        <td className="p-3">
                          {index === 0 
                            ? `$0 - $${bracket.threshold.toLocaleString()}`
                            : `$${(taxBrackets[filingStatus][index-1].threshold + 1).toLocaleString()} - $${bracket.threshold.toLocaleString()}`
                          }
                        </td>
                        <td className="text-right p-3">{(bracket.rate * 100).toFixed(1)}%</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="p-3">
                        ${(taxBrackets[filingStatus][taxBrackets[filingStatus].length - 1].threshold + 1).toLocaleString()}+
                      </td>
                      <td className="text-right p-3">
                        {(taxBrackets[filingStatus][taxBrackets[filingStatus].length - 1].rate * 100).toFixed(1)}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {result && (
              <>
                <div className="glass-card p-6">
                  <h3 className="text-xl font-semibold mb-6">Withholding Summary</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-slate-400 mb-1">Annual Income</p>
                      <p className="text-2xl font-bold text-emerald-500">
                        ${calculateAnnualIncome().toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400 mb-1">Taxable Income</p>
                      <p className="text-2xl font-bold text-emerald-500">
                        ${result.taxableIncome.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400 mb-1">Annual Withholding</p>
                      <p className="text-2xl font-bold text-emerald-500">
                        ${result.annualWithholding.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400 mb-1">Per Paycheck</p>
                      <p className="text-2xl font-bold text-emerald-500">
                        ${result.perPaycheckWithholding.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="mt-6 pt-6 border-t border-slate-700">
                    <p className="text-sm text-slate-400 mb-1">Effective Tax Rate</p>
                    <p className="text-2xl font-bold text-emerald-500">
                      {result.effectiveRate.toFixed(1)}%
                    </p>
                  </div>
                </div>

                <div className="glass-card p-6">
                  <h3 className="text-xl font-semibold mb-6">Tax Distribution</h3>
                  {chartData && (
                    <FinancialChart
                      type="pie"
                      data={chartData}
                      height={300}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mt-8">
          <Newsletter 
            title="Stay Updated on Tax Changes"
            subtitle="Get the latest tax updates and withholding strategies delivered to your inbox."
          />
        </div>
      </div>
    </div>
  );
};

export default WithholdingCalculator;